import {
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Box,
  createTheme,
  ThemeProvider,
  Typography,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import {
  Clear as ClearIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormControl } from "react-bootstrap";
import Button from "@mui/material/Button";
import {
  FilterItem,
  MultiSelectDropdown,
} from "src/components/multiSelectDropdown";
import { ProgramFilterDialog } from "src/components/dialog/ProgramFilterDialog";
import {
  Hcp,
  ProgramFilters,
  ProgramFiltersWithCountrySpecificFilters,
} from "src/types/programFilters";
import AffiliateService from "src/services/affiliate.service";
import {
  AffiliateProgramResponse,
  ProgramDetail,
} from "src/types/affiliatePrograms";
import { InfiniteScrollList } from "src/components/infiniteScroll";
import { AuthContext } from "src/utils/authContext";
import Loader from "src/components/loader/loader";

// 1. Theme Configuration
const customTheme = createTheme({
  palette: {
    primary: {
      main: "#5c6bc0", // Indigo
    },
    secondary: {
      main: "#4db6ac", // Teal
    },
    background: {
      default: "#f4f6f8",
    },
  },
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 3,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          // Fix for the double scrollbar issue by ensuring only one element manages overflow
          paddingTop: 0,
          paddingBottom: 0,
          maxHeight: 320, // Set max height on the list itself
          overflowY: "auto",
        },
      },
    },
  },
});

const { getCtvQuarters, getCountries, getHcps, getData, getProgramNumbers } =
  AffiliateService();

export const AffiliatePage = () => {
  const { checkAuthentication, user } = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [quarters, setQuarters] = useState<string[]>([]);
  const [countries, setCountries] = useState([]);
  const [hcps, setHcps] = useState<Hcp[]>([]);
  const [programNumbers, setProgramNumbers] = useState<string[]>([]);
  const [programs, setPrograms] = useState<ProgramDetail[]>([]);
  const page = useRef(0);
  const [hcpsForCountry, setHcpsForCountry] = useState(hcps);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);

  // Filter selections
  const [selectedQuarters, setSelectedQuarters] = useState<string[]>([]);
  const [selectedHcps, setSelectedHcps] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedProgramNumbers, setSelectedProgramNumbers] = useState<
    string[]
  >([]);

  // Dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Memo
  // Combine all selected IDs into a single array for rendering chips
  const allSelectedFilters = useMemo(
    () => [
      ...selectedQuarters,
      ...selectedCountries,
      ...selectedHcps,
      ...selectedProgramNumbers,
    ],
    [selectedQuarters, selectedHcps, selectedCountries, selectedProgramNumbers],
  );

  // Unified map of ID to its setter function for dynamic removal
  const setterMap = useMemo(
    () =>
      new Map<string, React.Dispatch<React.SetStateAction<string[]>>>([
        ...selectedQuarters.map((item) => [item, setSelectedQuarters] as const),
        ...selectedCountries.map(
          (item) => [item, setSelectedCountries] as const,
        ),
        ...selectedHcps.map((item) => [item, setSelectedHcps] as const),
        ...selectedProgramNumbers.map(
          (item) => [item, setSelectedProgramNumbers] as const,
        ),
      ]),
    [selectedQuarters, selectedHcps, selectedCountries, selectedProgramNumbers],
  );

  const handleRemovePill = useCallback(
    (idToRemove: string) => {
      // Find the correct setter function for the item being removed
      const setter = setterMap.get(idToRemove);
      if (setter) {
        setter((prevIds) => prevIds.filter((id) => id !== idToRemove));
      }
    },
    [setterMap],
  );

  // Clear All Filters
  const handleClearAllFilters = useCallback(() => {
    setSelectedQuarters([]);
    setSelectedHcps([]);
    setSelectedCountries([]);
    setSelectedProgramNumbers([]);
  }, []);

  const handleFilterSelection = ({
    quarters,
    countries,
    hcps,
    hcpsForCountry,
    programNumbers,
  }: ProgramFiltersWithCountrySpecificFilters) => {
    setSelectedCountries(countries);
    setSelectedHcps(hcps);
    setSelectedQuarters(quarters);
    setHcpsForCountry(hcpsForCountry);
    setSelectedProgramNumbers(programNumbers);
    setIsDialogOpen(false);
  };

  const getInitialData = async () => {
    setLoading(true);
    page.current = 0;
    const programs = await getData({
      page: 0,
      ...(selectedCountries.length > 0 && {
        countries: selectedCountries.join(","),
      }),
      ...(selectedQuarters.length > 0 && {
        ctvquarters: selectedQuarters.join(","),
      }),
      ...(selectedHcps.length > 0 && { hcps: selectedHcps.join(",") }),
      ...(selectedProgramNumbers.length > 0 && {
        programnumbers: selectedProgramNumbers.join(","),
      }),
    });

    setPrograms(programs.hcpProgramDetails);
    setHasMore(programs.has_more);
    setLoading(false);
  };

  const getFilters = async () => {
    setLoadingFilters(true);
    const [ctvQuarters, countries, hcps, programNumbers] = await Promise.all([
      getCtvQuarters(),
      getCountries(),
      getHcps(),
      getProgramNumbers(),
    ]);
    setQuarters(ctvQuarters.ctvQuartersList);
    setCountries(countries.countriesList);
    setHcps(hcps.hcpsList);
    setProgramNumbers(programNumbers.programNumbersList);
    setLoadingFilters(false);
  };

  const loadMore = async () => {
    if (loading || loadingMore) {
      return;
    }

    setLoadingMore(true);
    const programs = await getData({
      page: ++page.current,
      ...(selectedCountries.length > 0 && {
        countries: selectedCountries.join(","),
      }),
      ...(selectedQuarters.length > 0 && {
        ctvquarters: selectedQuarters.join(","),
      }),
      ...(selectedHcps.length > 0 && { hcps: selectedHcps.join(",") }),
      ...(selectedProgramNumbers.length > 0 && {
        programnumbers: selectedProgramNumbers.join(","),
      }),
    });
    setPrograms((prev) => [...prev, ...programs.hcpProgramDetails]);
    setHasMore(programs.has_more);
    setLoadingMore(false);
  };

  useEffect(() => {
    getFilters();
  }, []);

  useEffect(() => {
    getInitialData();
  }, [JSON.stringify(allSelectedFilters)]);

  useEffect(() => {
    if (selectedCountries.length > 0) {
      setSelectedHcps(
        hcpsForCountry
          .filter((hcp) => selectedHcps.includes(hcp.name))
          .map((hcp) => hcp.name),
      );
    }
  }, [hcpsForCountry]);

  useEffect(() => {
    if (selectedCountries.length > 0) {
      setHcpsForCountry(
        hcps.filter((hcp) => selectedCountries.includes(hcp.country)),
      );
    } else {
      setHcpsForCountry(hcps);
    }
  }, [selectedCountries]);

  return (
    <ThemeProvider theme={customTheme}>
      {isDialogOpen && (
        <ProgramFilterDialog
          countries={countries}
          hcps={hcps}
          quarters={quarters}
          programNumbers={programNumbers}
          previousSelectedCountries={selectedCountries}
          previousSelectedHcps={selectedHcps}
          previousSelectedQuarters={selectedQuarters}
          previousSelectedProgramNumbers={selectedProgramNumbers}
          open={isDialogOpen}
          onApply={handleFilterSelection}
          onCancel={() => {
            setIsDialogOpen(false);
          }}
        />
      )}
      <div className="wrapper">
        {/* Dropdown and Button container */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            mb: { xs: 2, lg: 0 },
            mr: { lg: 2 },
            width: "100vw",
          }}
        >
          <Button
            aria-controls={isDialogOpen ? "filter-menu-" : undefined}
            aria-haspopup="true"
            aria-expanded={isDialogOpen ? "true" : undefined}
            onClick={() => setIsDialogOpen(true)}
            variant={allSelectedFilters.length > 0 ? "contained" : "outlined"}
            color="primary"
            startIcon={<FilterListIcon />}
            sx={{
              position: "absolute",
              left: 0,
              borderRadius: 2,
              textTransform: "none",
              ...(allSelectedFilters.length > 0 && {
                bgcolor: "#5c6bc0",
                "&:hover": { bgcolor: "#4e5baa" },
              }),
              ...(loadingFilters && {
                bgcolor: "#adadadff",
                "&:hover": { bgcolor: "#adadadff" },
              }),
            }}
            disabled={loadingFilters}
          >
            Open Filter
          </Button>
          <Typography variant="h4">CTV List for completion</Typography>
        </Box>

        {/* Applied Filters / Pills Display */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mt: { xs: 2, lg: 1 },
          }}
        >
          {allSelectedFilters.map((item) => (
            <Chip
              key={item}
              label={item}
              onDelete={() => handleRemovePill(item)}
              color="primary"
              variant="filled"
              size="medium"
              deleteIcon={<ClearIcon />}
              sx={{
                bgcolor: "secondary.main",
                color: "white",
                "& .MuiChip-deleteIcon": { color: "white" },
              }}
            />
          ))}

          {allSelectedFilters.length > 0 && (
            <Button
              onClick={handleClearAllFilters}
              variant="outlined"
              color="error"
              size="small"
              startIcon={<ClearIcon />}
              sx={{ textTransform: "none", ml: { xs: 0, md: 1 } }}
            >
              Clear All ({allSelectedFilters.length})
            </Button>
          )}
        </Box>

        {/* Loading Message with smooth transition */}
        {loading ? (
          <div className="loading-container fade-in">
            <Loader />
          </div>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ overflow: "visible" }}
            className="table-container"
          >
            <Table>
              <TableHead
                sx={{
                  position: "sticky",
                  top: 60,
                  zIndex: 30,
                  borderTopLeftRadius: 5,
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{ borderTopLeftRadius: 12 }}
                    className="css-mlny8k MuiTableCell-head"
                  >
                    Program Number
                  </TableCell>
                  <TableCell className="css-mlny8k MuiTableCell-head">
                    Program Name
                  </TableCell>
                  <TableCell
                    align="center"
                    className="css-mlny8k MuiTableCell-head"
                  >
                    Program Type
                  </TableCell>
                  <TableCell
                    align="center"
                    className="css-mlny8k MuiTableCell-head"
                  >
                    Product Name
                  </TableCell>
                  <TableCell className="css-mlny8k MuiTableCell-head">
                    Patient ID
                  </TableCell>
                  <TableCell
                    align="center"
                    className="css-mlny8k MuiTableCell-head"
                  >
                    Receipt Nr
                  </TableCell>
                  <TableCell className="css-mlny8k MuiTableCell-head">
                    Events Reported
                  </TableCell>
                  <TableCell className="css-mlny8k MuiTableCell-head">
                    Response Received
                  </TableCell>
                  <TableCell className="css-mlny8k MuiTableCell-head">
                    Response Received Date
                  </TableCell>
                  <TableCell className="css-mlny8k MuiTableCell-head">
                    Country
                  </TableCell>
                  <TableCell
                    sx={{ borderTopRightRadius: 12 }}
                    className="css-mlny8k MuiTableCell-head"
                  >
                    Submitted By
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {programs.length > 0 ? (
                  <InfiniteScrollList<ProgramDetail>
                    items={programs}
                    renderItem={(row, index) => (
                      <TableRow key={index}>
                        <TableCell className="css-mlny8k">
                          {row.program_number}
                        </TableCell>
                        <TableCell className="css-mlny8k">
                          {row.program}
                        </TableCell>
                        <TableCell align="center" className="css-mlny8k">
                          {row.program_type}
                        </TableCell>
                        <TableCell align="center" className="css-mlny8k">
                          {row.products}
                        </TableCell>
                        <TableCell className="css-mlny8k">
                          {row.patient_id}
                        </TableCell>
                        <TableCell align="center" className="css-mlny8k">
                          {row.lrn ?? "-"}
                        </TableCell>
                        <TableCell
                          sx={{ maxWidth: "30vw", wordWrap: "break-word" }}
                          className="css-mlny8k"
                        >
                          {row.events_reported ?? "-"}
                        </TableCell>
                        <TableCell className="css-mlny8k">
                          {row.date_of_response_received ? "Yes" : "No"}
                        </TableCell>
                        <TableCell className="css-mlny8k">
                          {row.date_of_response_received ?? "-"}
                        </TableCell>
                        <TableCell className="css-mlny8k">
                          {row.country}
                        </TableCell>
                        <TableCell className="css-mlny8k">
                          {row.date_of_response_received
                            ? row.submittedBy || row.tpEmailId
                            : row.tpEmailId}
                        </TableCell>
                      </TableRow>
                    )}
                    hasMore={hasMore}
                    loadMore={() => {
                      console.log("Load more");
                      loadMore();
                    }}
                    loading={loadingMore}
                  />
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} style={{ textAlign: "center" }}>
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    </ThemeProvider>
  );
};
